<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';

import Banner from 'dashboard/components/ui/Banner.vue';

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
  isOnPrivateNote: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');

const assignedAgent = computed(() => currentChat.value?.meta?.assignee);

const isUserTyping = computed(
  () => props.message !== '' && !props.isOnPrivateNote
);
const currentAssigneeType = computed(() => {
  if (currentChat.value?.meta?.assignee_type) {
    return currentChat.value.meta.assignee_type;
  }
  if (assignedAgent.value?.assignee_type) {
    return assignedAgent.value.assignee_type;
  }
  return assignedAgent.value?.type === 'ai_agent' ? 'AiAgent' : 'User';
});
const isAssignedToAiAgent = computed(
  () => currentAssigneeType.value === 'AiAgent'
);
const isUnassigned = computed(() => !assignedAgent.value);
const isAssignedToOtherAgent = computed(
  () =>
    !isAssignedToAiAgent.value &&
    assignedAgent.value?.id !== currentUser.value?.id
);

const showSelfAssignBanner = computed(() => {
  return (
    isUserTyping.value &&
    !isAssignedToAiAgent.value &&
    (isUnassigned.value || isAssignedToOtherAgent.value)
  );
});

const showAiOwnershipBanner = computed(
  () => !props.isOnPrivateNote && isAssignedToAiAgent.value
);

const showBotHandoffBanner = computed(
  () =>
    isUserTyping.value &&
    currentChat.value?.status === wootConstants.STATUS_TYPE.PENDING
);

const contactCustomAttributes = computed(
  () => currentChat.value?.meta?.sender?.custom_attributes || {}
);

const showWhatsappOptOutBanner = computed(() => {
  return (
    !props.isOnPrivateNote &&
    (contactCustomAttributes.value.opt_out === true ||
      contactCustomAttributes.value.whatsapp_opt_out === true)
  );
});

const botHandoffActionLabel = computed(() => {
  return assignedAgent.value?.id === currentUser.value?.id
    ? t('CONVERSATION.BOT_HANDOFF_REOPEN_ACTION')
    : t('CONVERSATION.BOT_HANDOFF_ACTION');
});

const aiOwnershipMessage = computed(() => {
  return t('CONVERSATION.AI_OWNERSHIP_MESSAGE', {
    agentName: assignedAgent.value?.name || t('CONVERSATION.BOT'),
  });
});

const currentUserAssignee = computed(() => {
  const { avatar_url, ...rest } = currentUser.value || {};
  return { ...rest, thumbnail: avatar_url, assignee_type: 'User' };
});

const assignConversation = async (agent, assigneeType = 'User') => {
  const agentId = agent ? agent.id : null;
  store.dispatch('setCurrentChatAssignee', {
    conversationId: currentChat.value?.id,
    assignee: agent,
    assigneeType,
  });
  await store.dispatch('assignAgent', {
    conversationId: currentChat.value?.id,
    agentId,
    assigneeType,
  });
};

const selfAssignConversation = async () => {
  await assignConversation(currentUserAssignee.value, 'User');
};

const needsAssignmentToCurrentUser = computed(() => {
  return isUnassigned.value || isAssignedToOtherAgent.value;
});

const onClickSelfAssign = async () => {
  try {
    await selfAssignConversation();
    useAlert(t('CONVERSATION.CHANGE_AGENT'));
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
  }
};

const reopenConversation = async () => {
  await store.dispatch('toggleStatus', {
    conversationId: currentChat.value?.id,
    status: wootConstants.STATUS_TYPE.OPEN,
  });
};

const onClickBotHandoff = async () => {
  try {
    await reopenConversation();

    if (needsAssignmentToCurrentUser.value) {
      await selfAssignConversation();
    }

    useAlert(t('CONVERSATION.BOT_HANDOFF_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.BOT_HANDOFF_ERROR'));
  }
};

const onClickAssumeAiConversation = async () => {
  try {
    await selfAssignConversation();
    useAlert(t('CONVERSATION.BOT_HANDOFF_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.BOT_HANDOFF_ERROR'));
  }
};
</script>

<template>
  <Banner
    v-if="showWhatsappOptOutBanner"
    color-scheme="alert"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="$t('CONVERSATION.REPLYBOX.WHATSAPP_OPT_OUT.BANNER')"
  />
  <Banner
    v-if="showAiOwnershipBanner"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="aiOwnershipMessage"
    has-action-button
    :action-button-label="$t('CONVERSATION.AI_OWNERSHIP_ASSUME')"
    @primary-action="onClickAssumeAiConversation"
  />
  <Banner
    v-if="
      showSelfAssignBanner && !showBotHandoffBanner && !showAiOwnershipBanner
    "
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="$t('CONVERSATION.NOT_ASSIGNED_TO_YOU')"
    has-action-button
    :action-button-label="$t('CONVERSATION.ASSIGN_TO_ME')"
    @primary-action="onClickSelfAssign"
  />
  <Banner
    v-if="showBotHandoffBanner"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="$t('CONVERSATION.BOT_HANDOFF_MESSAGE')"
    has-action-button
    :action-button-label="botHandoffActionLabel"
    @primary-action="onClickBotHandoff"
  />
</template>
